import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  DeleteUserCredentialsRequest,
  GetUserCredentialsResponse,
  UserCredentialModel,
} from "services/KnowfullyAdminClient";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { useQueryClient } from "react-query";
import EditUserCredentialDialog from "./EditUserCredentialDialog";

type UserCredentialListProps = {
  userId: string;
  readonly?: boolean;
};

const UserCredentialList = ({ userId, readonly }: UserCredentialListProps) => {
  const theme = useTheme();
  const { formatMessage: t } = useIntl();
  const inheritedCredentialTooltip = t({ id: "inheritedCredentialTooltip" });
  const { userCredentialService } = useGlobalContext();
  const [expanded, setExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [
    userCredentialToDelete,
    setUserCredentialToDelete,
  ] = useState<UserCredentialModel>();
  const [
    userCredentialToEdit,
    setUserCredentialToEdit,
  ] = useState<UserCredentialModel>();
  const queryClient = useQueryClient();

  const { data, isFetching } = useStrictQuery<GetUserCredentialsResponse>(
    ["getUserCredentials", userId],
    () => userCredentialService.getUserCredentials(userId),
    { enabled: expanded }
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "userCredentialTypeDisplayName",
      type: "text",
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "externalUserId",
      type: "text",
      label: t({ id: "externalUserId" }),
    },
    {
      accessor: "applicationName",
      type: "text",
      label: t({ id: "applicationLabel" }),
    },
  ];

  if (!readonly) {
    cells.push({
      accessor: "value",
      type: "node",
      align: "right",
      width: 80,
      label: "",
      View: ({ value }: { value: UserCredentialModel }) =>
        value?.isInheritedFromUserAccount ? null : (
          <BasicActionsThreeDotsMenu
            onEdit={() => {
              setUserCredentialToEdit(value);
              setEditDialogOpen(true);
            }}
            onDelete={() => {
              setUserCredentialToDelete(value);
            }}
          />
        ),
    });
  }

  const rows =
    data?.userCredentials?.map((c) => ({
      ...c,
      id: c.id!,
      color: c.isInheritedFromUserAccount ? theme.palette.grey[200] : undefined,
      tooltip: c.isInheritedFromUserAccount
        ? inheritedCredentialTooltip
        : undefined,
    })) ?? [];

  const handleDelete = async () => {
    await userCredentialService.deleteUserCredentials(
      new DeleteUserCredentialsRequest({
        id: userCredentialToDelete?.id,
        organizationUserId: userId,
      })
    );
    queryClient.invalidateQueries("getUserCredentials");
    setUserCredentialToDelete(undefined);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="userCredentials" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={2}>
            {!readonly && (
              <>
                <Box mb={3} textAlign="right">
                  <Button
                    variant="outlined"
                    type="submit"
                    color="primary"
                    onClick={() => setEditDialogOpen(true)}
                  >
                    <T id="addUserCredential" />
                  </Button>
                </Box>

                <DeleteEntityDialog
                  dialogTitleKey="deleteUserCredential"
                  contentKey="areYouSureToDeleteUserCredential"
                  open={!!userCredentialToDelete}
                  handleDelete={handleDelete}
                  onCancel={() => setUserCredentialToDelete(undefined)}
                />

                <EditUserCredentialDialog
                  open={editDialogOpen}
                  organizationUserId={userId}
                  userCredential={userCredentialToEdit}
                  close={() => {
                    setUserCredentialToEdit(undefined);
                    setEditDialogOpen(false);
                  }}
                />
              </>
            )}

            <SortableTable
              {...{
                rows,
                loading: isFetching,
                dense: true,
                cells,
                tableRowPerPage: 5,
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default withErrorBoundary(UserCredentialList);
